import { DatePicker, Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import { graphql } from 'client/gql';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { isAfter, isBefore } from 'utils/dateCalculations';
import * as Yup from 'yup';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import type { SchoolyearData } from '../../pages/Institution/subpages/InstitutionSchoolyearsSubpage';
import { CONNECT_DATE_TEMPLATE } from '../../utils/connectLib';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';

const CreateSchoolyearMutationDocument = graphql(`
  mutation CreateBpSchoolyear($input: BpSchoolyearCreateInput!) {
    createBpSchoolYear(input: $input) {
      uuid
      __typename
    }
  }
`);
const UpdateSchoolyearMutationDocument = graphql(`
  mutation UpdateBpSchoolyear($uuid: ID!, $update: BpSchoolyearUpdateInput!) {
    updateBpSchoolYear(uuid: $uuid, update: $update) {
      uuid
      __typename
      name
      shortName
      start
      end
      createdAt
      managedBy
      autoSync
    }
  }
`);

type SchoolyearFormType = {
  name: string;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  organization?: string;
};

type SchoolyearFormProps = {
  onClose: () => void;
  schoolyears?: SchoolyearData[];
  selectedSchoolyearUuid?: string | null;
};

export const SchoolyearForm = ({ onClose, schoolyears, selectedSchoolyearUuid }: SchoolyearFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();

  const [overlappingError, setOverlappingError] = useState<string | null>(null);

  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const perms = usePermissionChecker();
  const [, updateSchoolyear] = useMutation(UpdateSchoolyearMutationDocument);
  const [, createSchoolyear] = useMutation(CreateSchoolyearMutationDocument);

  const schoolyear = schoolyears?.find((sy) => sy.uuid === selectedSchoolyearUuid);

  const formikValues: SchoolyearFormType = {
    name: schoolyear?.name ?? '',
    start: schoolyear?.start !== '' ? dayjs(schoolyear?.start).startOf('day') : dayjs(),
    end: schoolyear?.end && schoolyear?.end !== '' ? dayjs(schoolyear?.end).startOf('day') : dayjs().add(1, 'year'),
  };

  const handleSubmit = async (values: typeof formikValues) => {
    if (values.start && values.end) {
      const overlapping = schoolyears?.find((sy) => {
        if (schoolyear && sy.uuid === schoolyear.uuid) return false;
        const syStart = dayjs(sy.start);
        const syEnd = dayjs(sy.end);
        return values.start.isBefore(syEnd) && values.end.isAfter(syStart);
      });
      if (overlapping) {
        setOverlappingError(
          t('validation.time.schoolyearOverlap', { name: overlapping.name, interpolation: { escapeValue: false } }), // 2025/2026 wird sonst zu 2025&#x2F;2026
        );
        return;
      }
    }

    if (schoolyear && perms?.canUpdateSchoolyear({ uuid: organizationUuid })) {
      const { error } = await updateSchoolyear({
        update: {
          name: values.name,
          start: values.start.format(CONNECT_DATE_TEMPLATE),
          end: values.end.format(CONNECT_DATE_TEMPLATE),
        },
        uuid: schoolyear?.uuid,
      });

      error ? showErrorToast(error) : showSuccessToast(t('schoolyears.saved'));
    }

    if (!schoolyear && perms?.canCreateSchoolyear({ uuid: organizationUuid })) {
      const { error } = await createSchoolyear({
        input: {
          name: values.name,
          end: values.end.toString(),
          start: values.start.toString(),
          organization: organizationUuid,
          foreignRefId: [],
        },
      });

      error ? showErrorToast(error) : showSuccessToast(t('schoolyears.saved'));
    }

    onClose();
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.required.designation')).min(2, t('validation.textTooShort')),
  });

  return (
    <Formik onSubmit={handleSubmit} initialValues={formikValues} validationSchema={validationSchema}>
      {({ resetForm, isSubmitting, errors, handleChange, values, setFieldValue, setFieldTouched, setFieldError }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <Input
                  label={t('common.designation')}
                  onChange={handleChange}
                  name='name'
                  value={values.name}
                  error={errors.name}
                />
              </GridRow>
              <GridRow spacingTop='s'>
                <GridColumn width={6}>
                  <DatePicker
                    className='mb-2'
                    label={t('common.start')}
                    onChange={async (e) => {
                      setOverlappingError(null);
                      await setFieldValue('start', dayjs(e));
                      await setFieldTouched('start', true);
                      if (isAfter(e, values.end)) {
                        setFieldError('start', t('validation.time.startDateAfterEnd'));
                      } else {
                        setFieldError('start', undefined);
                      }
                    }}
                    error={errors.start as string}
                    name='start'
                    value={values.start.toDate()}
                    showMonthYearDropdown
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <DatePicker
                    className='mb-2'
                    label={t('common.end')}
                    onChange={async (e) => {
                      setOverlappingError(null);
                      await setFieldValue('end', dayjs(e));
                      await setFieldTouched('end', true);
                      if (isBefore(e, values.start)) {
                        setFieldError('end', t('validation.time.endDateBeforeStart'));
                      } else {
                        setFieldError('end', undefined);
                      }
                    }}
                    error={errors.end as string}
                    name='end'
                    value={values.end.toDate()}
                    showMonthYearDropdown
                  />
                </GridColumn>
              </GridRow>
            </Grid>
            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              errorText={overlappingError}
              isLoading={isSubmitting}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
